import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, InputAdornment, Button, IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentIcon from '@mui/icons-material/Payment';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import GlassCard from '../ui/glass/components/GlassCard';
import PageHeader from '../components/common/PageHeader';
import StatusChip from '../components/common/StatusChip';
import EmptyState from '../components/common/EmptyState';
import { Loading } from '../components/common/Loading';
import { usePermissions } from '../hooks/usePermissions';
import AddPaymentModal from '../components/modals/AddPaymentModal';
import { saveSalePayment, deleteSaleRecord } from './helpers/salesHandlers';
import { exportToExcel } from '../utils/exportToExcel';
import useCachedCollection, { invalidateCollection } from '../hooks/useCachedCollection';

// Satış kayıtları Excel kolon tanımı (salt-okunur export)
const saleExportColumns = [
    { header: 'Tarih', value: (s) => s.olusturmaTarihi, type: 'datetime', width: 18 },
    { header: 'Müşteri', value: (s) => s.musteriAdSoyad, width: 24 },
    { header: 'Ürün Sayısı', value: (s) => s.satilanUrunler?.length || 0, type: 'number', width: 12 },
    {
        header: 'Ürünler',
        value: (s) => (Array.isArray(s.satilanUrunler)
            ? s.satilanUrunler.map((u) => `${u.ad || u.urunAdi || 'Ürün'} x${u.miktar || 1}`).join(', ')
            : ''),
        width: 40,
    },
    { header: 'Toplam Tutar (₺)', value: (s) => s.toplamTutar, type: 'money', width: 16 },
    { header: 'Ödenen (₺)', value: (s) => s.odenenTutar, type: 'money', width: 14 },
    { header: 'Kalan (₺)', value: (s) => s.kalanTutar, type: 'money', width: 14 },
    { header: 'Ödeme Tipi', value: (s) => s.odemeTipi || 'Nakit', width: 14 },
    { header: 'Durum', value: (s) => ((s.kalanTutar || 0) > 0 ? 'Ödeme Bekliyor' : 'Tamamlandı'), width: 16 },
];

const UrunSatisKayitlari = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [visibleCount, setVisibleCount] = useState(25); // Faz 7: ekranda gösterilen satır sayısı
    const navigate = useNavigate();
    const { canDelete } = usePermissions();

    // Payment Modal State
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [selectedSale, setSelectedSale] = useState(null);

    // Faz 7: realtime onSnapshot yerine oturum-içi cache (oturumda 1 kez okunur).
    const { data: salesRaw, loading, refresh: refreshSales } = useCachedCollection('sales');
    // Eski Firestore orderBy("olusturmaTarihi","desc") sırasını koru (cache sırasız gelir).
    const sales = useMemo(() => {
        const toMs = (t) => (t?.toMillis ? t.toMillis() : (t?.seconds ? t.seconds * 1000 : 0));
        return [...salesRaw].sort((a, b) => toMs(b.olusturmaTarihi) - toMs(a.olusturmaTarihi));
    }, [salesRaw]);

    const handleOpenPaymentModal = (sale) => {
        setSelectedSale(sale);
        setIsPaymentModalOpen(true);
    };

    const handleSavePayment = async (paymentData) => {
        try {
            await saveSalePayment(selectedSale, paymentData);
            // Faz 7: satışı tazele; tahsilat transactions+customers'ı da değiştirdiğinden
            // o cache'leri geçersiz kıl (bakiye/cari ekranları bayatlamasın).
            await refreshSales();
            invalidateCollection('transactions');
            invalidateCollection('customers');
            setIsPaymentModalOpen(false);
            setSelectedSale(null);
        } catch (error) {
            console.error("Tahsilat kaydedilirken hata:", error);
            alert("Bir hata oluştu: " + error.message);
        }
    };

    const handleDelete = async (sale) => {
        const ok = await deleteSaleRecord(sale);
        if (ok) {
            // Faz 7: satış silme cascade (transactions/devices/customers/stocks) yaptığından
            // bu cache'leri de geçersiz kıl ki ilgili ekranlar güncel veriyi çeksin.
            await refreshSales();
            invalidateCollection('transactions');
            invalidateCollection('devices');
            invalidateCollection('customers');
            invalidateCollection('stocks');
        }
    };

    const filteredSales = sales.filter(sale =>
        sale.musteriAdSoyad?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Faz 7: ekranda yalnızca ilk `visibleCount` satışı göster (DOM'u hafifletir).
    // Arama TÜM satışlar üzerinde çalışır; export da tüm filtreli veriyi indirir.
    const visibleSales = filteredSales.slice(0, visibleCount);

    // Excel export (salt-okunur) — aktif aramaya uyan satışları indirir
    const handleExportVisible = () => {
        if (filteredSales.length === 0) {
            alert('Dışa aktarılacak satış kaydı bulunamadı.');
            return;
        }
        exportToExcel({ rows: filteredSales, columns: saleExportColumns, sheetName: 'Satışlar', fileName: 'Satis_Kayitlari', scopeLabel: 'Goruntulenen' });
    };

    // Excel export (salt-okunur) — aramadan bağımsız tüm satışları indirir
    const handleExportAll = () => {
        if (sales.length === 0) {
            alert('Dışa aktarılacak satış kaydı bulunamadı.');
            return;
        }
        exportToExcel({ rows: sales, columns: saleExportColumns, sheetName: 'Satışlar', fileName: 'Satis_Kayitlari', scopeLabel: 'Tumu' });
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        try {
            return format(timestamp.toDate(), 'dd MMM yyyy HH:mm', { locale: tr });
        } catch {
            return '-';
        }
    };

    const formatCurrency = (amount) => {
        return (amount || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' });
    };

    if (loading) {
        return <Loading message="Satışlar yükleniyor..." />;
    }

    return (
        <Box sx={{ p: { xs: 1, md: 1 } }}>
            {/* Header Section */}
            <PageHeader
                title="Ürün Satış Kayıtları"
                subtitle="Tüm ürün satışlarını buradan yönetebilirsiniz."
                icon={<ReceiptLongIcon />}
            >
                        <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Müşteri Ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                backgroundColor: 'background.paper',
                                borderRadius: 1
                            }}
                        />
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={refreshSales}
                            disabled={loading}
                        >
                            Yenile
                        </Button>
                        <Button
                            variant="outlined"
                            color="success"
                            startIcon={<FileDownloadIcon />}
                            onClick={handleExportVisible}
                        >
                            Görüneni İndir
                        </Button>
                        <Button
                            variant="outlined"
                            color="success"
                            startIcon={<FileDownloadIcon />}
                            onClick={handleExportAll}
                        >
                            Tümünü İndir
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => navigate('/satis-olustur')}
                            sx={{ px: 3 }}
                        >
                            Yeni Satış
                        </Button>
            </PageHeader>

            {/* Table Section */}
            <GlassCard sx={{ overflow: 'hidden', p: 0 }}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Tarih</TableCell>
                                <TableCell>Müşteri</TableCell>
                                <TableCell>Ürün Sayısı</TableCell>
                                <TableCell align="right">Toplam Tutar</TableCell>
                                <TableCell align="right">Ödenen</TableCell>
                                <TableCell align="right">Kalan</TableCell>
                                <TableCell>Ödeme Tipi</TableCell>
                                <TableCell>Durum</TableCell>
                                <TableCell align="center" sx={{ width: 150 }}>İşlemler</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredSales.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} sx={{ border: 0 }}>
                                        <EmptyState
                                            icon={<ReceiptLongIcon />}
                                            message="Henüz satış kaydı bulunmuyor."
                                            description={searchTerm ? 'Aramanızla eşleşen kayıt yok.' : 'Yeni bir satış oluşturarak başlayın.'}
                                        />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                visibleSales.map((sale) => (
                                    <TableRow
                                        hover
                                        key={sale.id}
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/satis-detay/${sale.id}`)}
                                    >
                                        <TableCell>{formatDate(sale.olusturmaTarihi)}</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>{sale.musteriAdSoyad}</TableCell>
                                        <TableCell>
                                            <StatusChip
                                                label={`${sale.satilanUrunler?.length || 0} ürün`}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                                            {formatCurrency(sale.toplamTutar)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: 'success.main', fontWeight: 500 }}>
                                            {formatCurrency(sale.odenenTutar)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: sale.kalanTutar > 0 ? 'error.main' : 'text.secondary', fontWeight: 500 }}>
                                            {formatCurrency(sale.kalanTutar)}
                                        </TableCell>
                                        <TableCell>
                                            <StatusChip
                                                label={sale.odemeTipi || 'Nakit'}
                                                color="info"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <StatusChip
                                                label={sale.kalanTutar > 0 ? 'Ödeme Bekliyor' : 'Tamamlandı'}
                                                color={sale.kalanTutar > 0 ? 'warning' : 'success'}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
                                                {sale.kalanTutar > 0 && (
                                                    <IconButton
                                                        size="small"
                                                        color="success"
                                                        title="Tahsilat Ekle"
                                                        onClick={() => handleOpenPaymentModal(sale)}
                                                    >
                                                        <PaymentIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => navigate(`/satis-detay/${sale.id}`)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                {canDelete() && (
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDelete(sale)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {visibleCount < filteredSales.length && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 2 }}>
                        <Typography variant="caption" color="text.secondary">{visibleSales.length} / {filteredSales.length} gösteriliyor</Typography>
                        <Button variant="outlined" onClick={() => setVisibleCount(c => c + 25)}>Daha Fazla Yükle</Button>
                    </Box>
                )}
            </GlassCard>

            <AddPaymentModal
                isOpen={isPaymentModalOpen}
                onClose={() => setIsPaymentModalOpen(false)}
                onSave={handleSavePayment}
                kalanTutar={selectedSale?.kalanTutar || 0}
                customerName={selectedSale?.musteriAdSoyad}
            />
        </Box>
    );
};

export default UrunSatisKayitlari;
